import React, { FC, useState, useMemo } from 'react';
import { IconButton, TableRow } from '@mui/material';
import { DataTableCell, DataTableValue } from '../../data-table/data-table.componet';
import DataTableTextField from '../../data-table/data-table-text-field.component';
import DataTableAutocomplete, { DataTableAutocompleteOption } from '../../data-table/data-table-autocomplete.component';
import DeleteIcon from '@mui/icons-material/Delete';
import { ProductionStep } from 'models/order';

type ProductionValueReportRowProps = {
    isProductionFinished: boolean;
    fromInternalPage?: boolean;
    isTechnicianAdmin: boolean;
    productionValueReportRow: ProductionStep;
    onStepChange: (rawIndex: number, field: string, value: any) => void;
    onDeleteStep: (rawIndex: number) => void;
    rawIndex: number;
    actionDeductionValue?: number;
};

const ProductionValueReportRow: FC<ProductionValueReportRowProps> = ({
    isProductionFinished,
    fromInternalPage,
    isTechnicianAdmin,
    productionValueReportRow,
    onStepChange,
    onDeleteStep,
    rawIndex,
    actionDeductionValue = 0,
}) => {
    const [selectedCell, setSelectedCell] = useState<string>();
    const isReadonly = isProductionFinished && fromInternalPage && !isTechnicianAdmin;
    const stepDeductionValue = actionDeductionValue * ((productionValueReportRow.valuePercent || 0) / 100);

    const isCellSelected = (colId: string) => selectedCell === colId;

    const technicianOptions = useMemo<DataTableAutocompleteOption[]>(
        () =>
            productionValueReportRow.technicians?.map((technician) => ({
                id: technician._id || '',
                key: technician.fullName || '',
            })) || [],
        [productionValueReportRow.technicians],
    );

    const selectedTechnicianValue = useMemo<DataTableAutocompleteOption | null>(() => {
        if (!productionValueReportRow.technician) return null;
        const tech = productionValueReportRow.technicians?.find(
            (t) => t._id?.toString() === productionValueReportRow.technician?.toString(),
        );
        if (!tech) return null;
        return { id: tech._id || '', key: tech.fullName || '' };
    }, [productionValueReportRow.technician, productionValueReportRow.technicians]);

    return (
        <TableRow key={productionValueReportRow.step}>
            <DataTableCell readonly>
                <DataTableValue>{productionValueReportRow.step}</DataTableValue>
            </DataTableCell>
            <DataTableCell readonly>
                <DataTableValue>{productionValueReportRow.department || ''}</DataTableValue>
            </DataTableCell>
            <DataTableCell>
                <DataTableAutocomplete
                    readonly={isReadonly}
                    selected={isCellSelected('technician')}
                    options={technicianOptions}
                    selectedValue={selectedTechnicianValue}
                    onSelectValue={(value) => onStepChange(rawIndex, 'technician', value?.id || null)}
                    onFocus={() => setSelectedCell('technician')}
                    onBlur={() => setSelectedCell(undefined)}
                />
            </DataTableCell>
            <DataTableCell>
                <DataTableTextField
                    readonly={isReadonly}
                    autoComplete="off"
                    variant="standard"
                    onFocus={() => setSelectedCell('valuePercent')}
                    fullWidth
                    selected={isCellSelected('valuePercent')}
                    alignRight
                    value={productionValueReportRow.valuePercent}
                    onChange={(e) =>
                        onStepChange(rawIndex, 'valuePercent', Math.max(0, parseInt(e.target.value, 10) || 0))
                    }
                    onBlur={() => setSelectedCell(undefined)}
                />
            </DataTableCell>
            <DataTableCell readonly>
                <DataTableValue>{stepDeductionValue.toFixed(2)}</DataTableValue>
            </DataTableCell>
            <DataTableCell width={15} align="center">
                <IconButton
                    disabled={isReadonly}
                    size="small"
                    data-test="production-value-report-row.delete-row"
                    onClick={() => onDeleteStep(rawIndex)}
                >
                    <DeleteIcon fontSize="small" />
                </IconButton>
            </DataTableCell>
        </TableRow>
    );
};

export default ProductionValueReportRow;
